const HABITA_API_URL_ENV = 'VITE_API_URL';
const COMMERCIAL_API_URL_ENV = 'VITE_COMMERCIAL_API_URL';
const MARKETING_API_URL_ENV = 'VITE_MARKETING_API_URL';

function getHabitaApiBaseUrl(): string {
  const apiUrl = String(import.meta.env.VITE_API_URL_ALT || import.meta.env.VITE_API_URL || '')
    .trim()
    .replace(/\/$/, '');

  if (!apiUrl) {
    throw new Error(`${HABITA_API_URL_ENV} não configurada.`);
  }

  return apiUrl;
}

function buildHabitaUrl(path: string): string {
  const cleanPath = path
    .trim()
    .replace(/^\/+/, '')
    .replace(/^habita4u\//, '');

  return `${getHabitaApiBaseUrl()}/${cleanPath}`;
}

function withQuery(
  url: string,
  params: Record<string, string | undefined>
): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value?.trim()) searchParams.set(key, value.trim());
  });
  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
}

function getMarketingApiBaseUrl(): string {
  const apiUrl = String(import.meta.env.VITE_MARKETING_API_URL || '')
    .trim()
    .replace(/\/$/, '');

  if (!apiUrl) {
    throw new Error(`${MARKETING_API_URL_ENV} não configurada.`);
  }

  return apiUrl;
}

function getCommercialApiBaseUrl(): string {
  const configuredUrl = String(import.meta.env.VITE_COMMERCIAL_API_URL || '')
    .trim()
    .replace(/\/$/, '');

  if (configuredUrl) return configuredUrl;

  const marketingUrl = String(import.meta.env.VITE_MARKETING_API_URL || '')
    .trim()
    .replace(/\/$/, '');

  if (marketingUrl.endsWith('/marketing')) {
    return `${marketingUrl.slice(0, -'/marketing'.length)}/commercial`;
  }

  if (!marketingUrl) {
    throw new Error(`${COMMERCIAL_API_URL_ENV} não configurada.`);
  }

  throw new Error(`${COMMERCIAL_API_URL_ENV} não configurada para o painel comercial.`);
}

export function getHabitaAuthLoginUrl(): string {
  return buildHabitaUrl('/auth/login');
}

export function getHabitaAuthLogoutUrl(): string {
  return buildHabitaUrl('/auth/logout');
}

export function getHabitaAuthUserUrl(): string {
  return buildHabitaUrl('/auth/user');
}

export function getHabitaAuthSessionUrl(): string {
  return buildHabitaUrl('/auth/session');
}

export function getHabitaDashboardInfoUrl(): string {
  return buildHabitaUrl('/dashboard/info');
}

export function getHabitaMarketingResourceUrl(resource: string): string {
  return `${getMarketingApiBaseUrl()}?resource=${encodeURIComponent(resource)}`;
}

export function getHabitaCommercialResourceUrl(resource: string): string {
  return `${getCommercialApiBaseUrl()}?resource=${encodeURIComponent(resource)}`;
}

export function getHabitaProcessListUrl(): string {
  return buildHabitaUrl('/process');
}

export function getHabitaResponsiblesUrl(): string {
  return buildHabitaUrl('/responsibles');
}

export function getHabitaUsersUrl(params: {
  profile?: string;
  searchText?: string;
} = {}): string {
  return withQuery(buildHabitaUrl('/users'), params);
}

export function getHabitaUsersGoalsUrl(params: {
  profile?: string;
  searchText?: string;
} = {}): string {
  return withQuery(buildHabitaUrl('/users/goals'), params);
}

export function getHabitaCompetencyUrl(period: string): string {
  return buildHabitaUrl(`/competencies/${encodeURIComponent(period)}`);
}

export function getHabitaCompetencyTeamGoalsUrl(period: string): string {
  return buildHabitaUrl(`/competencies/${encodeURIComponent(period)}/team-goals`);
}

export function getHabitaUserGoalsUpdateUrl(userId: string): string {
  return buildHabitaUrl(`/users/${encodeURIComponent(userId)}/goals`);
}

export function getHabitaProcessDetailUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}`);
}

export function getHabitaProcessFilesUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}/files`);
}

export function getHabitaProcessValidationUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}/validation`);
}

export function getHabitaProcessStatusUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}/status`);
}

export function getHabitaProcessStatusHistoryUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}/status-history`);
}

export function getHabitaProcessPendingItemsUrl(processId: string | number): string {
  return buildHabitaUrl(`/process/${encodeURIComponent(String(processId))}/pending-items`);
}

export function getHabitaFileDownloadUrl(filePath: string): string {
  if (/^https?:\/\//i.test(filePath)) {
    return filePath;
  }

  return buildHabitaUrl(filePath);
}
